import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

import HeroContentTable from '@/components/admin/hero/HeroContentTable';
import HeroContentForm from '@/components/admin/hero/HeroContentForm';
import FeaturedProductsManager from '@/components/admin/hero/FeaturedProductsManager';
import { BrandStoriesManager } from '@/components/admin/content/BrandStoriesManager';
import { LifestyleGalleryManager } from '@/components/admin/content/LifestyleGalleryManager';
import HeroSettingsPanel from '@/components/admin/hero/HeroSettingsPanel';
import HeroPreview from '@/components/admin/hero/HeroPreview';

const AdminHeroContent = () => {
  const { user, loading: authLoading } = useAuth();
  const { hasAdminAccess, loading: roleLoading } = useOptimizedAuth();
  const [editingContent, setEditingContent] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);

  if (authLoading || roleLoading) {
    return <div>Loading...</div>;
  }

  if (!user || !hasAdminAccess) {
    return <Navigate to="/" replace />;
  }

  const handleEdit = (content: any) => {
    setEditingContent(content);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingContent(null);
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingContent(null);
  };

  return (
    <>
      <Helmet>
        <title>Hero Content Management - Admin</title>
      </Helmet>
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Hero Content Management</h1>
          <p className="text-muted-foreground">
            Manage your homepage hero content, featured products, and settings
          </p>
        </div>

        <Tabs defaultValue="content" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="brand-stories">Stories</TabsTrigger>
            <TabsTrigger value="lifestyle">Gallery</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <h2 className="text-xl font-semibold mb-4">Hero Content Management</h2>
                <div className="p-6 border border-dashed border-muted rounded-lg text-center">
                  <p className="text-muted-foreground mb-4">
                    Create and manage hero content for your homepage
                  </p>
                  <button
                    onClick={handleAdd}
                    className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    Add New Content
                  </button>
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold mb-4">Existing Content</h2>
                <HeroContentTable 
                  onEdit={handleEdit}
                  onAdd={handleAdd}
                />
              </div>
            </div>

            {showForm && (
              <HeroContentForm 
                content={editingContent}
                onClose={handleClose}
              />
            )}
          </TabsContent>

        <TabsContent value="products">
          <FeaturedProductsManager />
        </TabsContent>

        <TabsContent value="brand-stories">
          <BrandStoriesManager />
        </TabsContent>

        <TabsContent value="lifestyle">
          <LifestyleGalleryManager />
        </TabsContent>

        <TabsContent value="settings">
          <HeroSettingsPanel />
        </TabsContent>

        <TabsContent value="preview">
          <HeroPreview />
        </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default AdminHeroContent;
